import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { Alert, Button, Card, EmptyState, IconButton, PageHeader, Pill, Skeleton, useConfirm, useToast } from '../ui';
import { CheckIcon, EditIcon, ImageIcon, PlusIcon, RefreshIcon, TrashIcon } from '../ui/icons';
import { sectionCrumb } from '../settings/SectionLayout';
import AIConfigurationModal from './AIConfigurationModal';

const apiError = (error) => error.response?.data?.error || error.response?.statusText || error.message;

/**
 * /admin/ai — the AI services that read photos for the import (deprecated
 * page, kept for administrators): the active one and its test, every
 * configuration, adding and editing them.
 */
function AIConfiguration() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConfiguration, setEditingConfiguration] = useState(null);
  const [testingConfiguration, setTestingConfiguration] = useState(null);

  useEffect(() => {
    fetchConfigurations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once, on arrival

  const fetchConfigurations = async () => {
    try {
      const response = await api.get('/ai-configurations');
      setConfigurations(response.data);
    } catch (error) {
      console.error('Error fetching AI configurations:', error);
      toast.error(t('aiConfig.errors.load', { error: apiError(error) }));
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async (configurationData) => {
    try {
      if (editingConfiguration) {
        const response = await api.put(`/ai-configurations/${editingConfiguration.id}`, configurationData);
        setConfigurations(configurations.map(config => (config.id === editingConfiguration.id ? response.data : config)));
      } else {
        const response = await api.post('/ai-configurations', configurationData);
        setConfigurations([...configurations, response.data]);
      }
      setShowModal(false);
      setEditingConfiguration(null);
      toast.success(t('aiConfig.saved', { name: configurationData.name }));
      // A configuration set as active changes the others: read them again.
      if (configurationData.is_active) fetchConfigurations();
    } catch (error) {
      console.error('Error saving AI configuration:', error);
      toast.error(t('aiConfig.errors.save', { error: apiError(error) }));
    }
  };

  const deleteConfiguration = async (configuration) => {
    if (!(await confirm({
      title: t('aiConfig.confirmDeleteTitle'),
      message: t('aiConfig.confirmDelete', { name: configuration.name }),
      confirmLabel: t('aiConfig.deleteButton'),
      danger: true,
    }))) return;
    try {
      await api.delete(`/ai-configurations/${configuration.id}`);
      setConfigurations(configurations.filter(config => config.id !== configuration.id));
    } catch (error) {
      console.error('Error deleting AI configuration:', error);
      toast.error(t('aiConfig.errors.delete', { error: error.response?.data?.error || error.message }));
    }
  };

  const setActiveConfiguration = async (configurationId) => {
    try {
      await api.post(`/ai-configurations/${configurationId}/activate`);
      fetchConfigurations();
    } catch (error) {
      console.error('Error setting active configuration:', error);
      toast.error(t('aiConfig.errors.activate', { error: error.response?.data?.error || error.message }));
    }
  };

  const testConfiguration = async (configuration) => {
    setTestingConfiguration(configuration.id);
    try {
      // The test runs on the active configuration: this one is made active for it, the previous one restored after.
      const wasActive = configurations.find(c => c.is_active);
      await api.post(`/ai-configurations/${configuration.id}/activate`);
      const response = await api.get('/test-claude');
      if (response.data.success) {
        toast.success(t('aiConfig.testOk', { type: response.data.api_type }), { title: t('aiConfig.testOkTitle', { name: configuration.name }) });
      } else {
        toast.error(response.data.error, { title: t('aiConfig.testFailedTitle', { name: configuration.name }) });
      }
      if (wasActive && wasActive.id !== configuration.id) {
        await api.post(`/ai-configurations/${wasActive.id}/activate`);
      }
      fetchConfigurations();
    } catch (error) {
      console.error('Error testing configuration:', error);
      toast.error(error.response?.data?.error || error.message, { title: t('aiConfig.testFailedTitle', { name: configuration.name }) });
    } finally {
      setTestingConfiguration(null);
    }
  };

  const formatApiType = (apiType) => (['claude', 'openai', 'custom'].includes(apiType) ? t(`aiConfig.types.${apiType}`) : apiType);
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString(i18n.resolvedLanguage || 'en', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const add = () => { setEditingConfiguration(null); setShowModal(true); };

  const header = (
    <PageHeader
      breadcrumb={[sectionCrumb(t, 'admin'), { label: t('nav.aiConfiguration') }]}
      title={t('nav.aiConfiguration')}
      subtitle={t('aiConfig.subtitle')}
      actions={<Button icon={PlusIcon} onClick={add}>{t('aiConfig.add')}</Button>}
    />
  );
  if (loading) return <>{header}<Skeleton height={240} radius="var(--radius-lg)" /></>;

  const activeConfig = configurations.find(config => config.is_active);
  return (
    <div>
      {header}
      <div className="settings-stack">
        {activeConfig ? (
          <Alert tone="success" title={t('aiConfig.activeTitle', { name: activeConfig.name })}
                 action={(
                   <Button size="sm" variant="secondary" icon={RefreshIcon} onClick={() => testConfiguration(activeConfig)}
                           loading={testingConfiguration === activeConfig.id}>
                     {t('aiConfig.test')}
                   </Button>
                 )}>
            {formatApiType(activeConfig.api_type)} · {activeConfig.model_name || t('aiConfig.defaultModel')}
          </Alert>
        ) : (
          <Alert tone="warning">{t('aiConfig.noActive')}</Alert>
        )}

        <Card as="section" padding="lg" aria-labelledby="ai-all">
          <h2 id="ai-all" className="settings-card__title">{t('aiConfig.all', { count: configurations.length })}</h2>
          {configurations.length === 0 ? (
            <EmptyState icon={ImageIcon} title={t('aiConfig.emptyTitle')} action={<Button icon={PlusIcon} onClick={add}>{t('aiConfig.add')}</Button>}>
              {t('aiConfig.empty')}
            </EmptyState>
          ) : (
            <div className="data-table-wrap">
              <table role="table" className="data-table data-table--stack">
                <thead>
                  <tr role="row">
                    <th role="columnheader" scope="col">{t('aiConfig.col.name')}</th>
                    <th role="columnheader" scope="col">{t('aiConfig.col.type')}</th>
                    <th role="columnheader" scope="col">{t('aiConfig.col.model')}</th>
                    <th role="columnheader" scope="col">{t('aiConfig.col.key')}</th>
                    <th role="columnheader" scope="col">{t('aiConfig.col.created')}</th>
                    <th role="columnheader" scope="col"><span className="visually-hidden">{t('accounts.col.actions')}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {configurations.map(config => (
                    <tr role="row" key={config.id}>
                      <th role="rowheader" scope="row">
                        {config.name} {config.is_active && <Pill tone="green">{t('aiConfig.active')}</Pill>}
                      </th>
                      <td role="cell" data-label={t('aiConfig.col.type')}>{formatApiType(config.api_type)}</td>
                      <td role="cell" data-label={t('aiConfig.col.model')}>
                        {config.model_name ? <code className="settings-code">{config.model_name}</code> : <span className="settings-muted">{t('aiConfig.defaultModel')}</span>}
                      </td>
                      <td role="cell" data-label={t('aiConfig.col.key')}>
                        {config.has_api_key ? <Pill tone="green">{t('aiConfig.keySet')}</Pill> : <Pill tone="danger">{t('aiConfig.noKey')}</Pill>}
                      </td>
                      <td role="cell" data-label={t('aiConfig.col.created')} className="accounts__date">{formatDate(config.created_at)}</td>
                      <td role="cell" className="data-table__actions">
                        <div className="accounts__actions">
                          <IconButton icon={RefreshIcon} size="sm" variant="ghost" onClick={() => testConfiguration(config)}
                                      disabled={testingConfiguration === config.id} label={t('aiConfig.testNamed', { name: config.name })} />
                          {!config.is_active && (
                            <Button size="sm" variant="secondary" icon={CheckIcon} onClick={() => setActiveConfiguration(config.id)}
                                    aria-label={t('aiConfig.activateNamed', { name: config.name })}>
                              {t('aiConfig.activate')}
                            </Button>
                          )}
                          <IconButton icon={EditIcon} size="sm" variant="ghost" label={t('aiConfig.editNamed', { name: config.name })}
                                      onClick={() => { setEditingConfiguration(config); setShowModal(true); }} />
                          <IconButton icon={TrashIcon} size="sm" variant="ghost" className="field-row__remove" disabled={!!config.is_active}
                                      title={config.is_active ? t('aiConfig.cannotDeleteActive') : undefined}
                                      label={t('aiConfig.deleteNamed', { name: config.name })} onClick={() => deleteConfiguration(config)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {showModal && (
        <AIConfigurationModal
          configuration={editingConfiguration}
          onSave={saveConfiguration}
          onClose={() => { setShowModal(false); setEditingConfiguration(null); }}
        />
      )}
    </div>
  );
}

export default AIConfiguration;
